import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  AUTH_LOGOUT_BROADCAST_CHANNEL,
  AUTH_LOGOUT_BROADCAST_KEY,
  authService,
} from '../../../services/auth/authService';
import { fetchProfile } from '../../../store/slices/profileSlice';
import { AppDispatch } from '../../../store/store';
import startActivityTimestampLogger, { startIdleMonitor } from '../../../services/auth/idleTrackerService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const stopAllRef = useRef<() => void>(() => {});
  const suppressRedirectOnIdleRef = useRef(false);

  const profileFetchedRef = useRef(false);
  const idleInitialisedRef = useRef(false);

  const crossTabLogoutRef = useRef(false);

  /**
   * Listens for logout broadcasts from other browser tabs.
   *  1. Clears this tab's sessionStorage (removes all tokens).
   *  2. Marks auth_forced_logout so Login waits before re-initiating SSO.
   *  3. Stops idle/activity tracking for this tab.
   */
  useEffect(() => {
    let channel: BroadcastChannel | null = null;

    const applyCrossTabLogout = (): void => {
      crossTabLogoutRef.current = true;
      // Clear first, then set the marker so Login knows to delay before re-auth.
      sessionStorage.clear();
      sessionStorage.setItem('auth_forced_logout', '1');
      try {
        stopAllRef.current?.();
      } catch {
        // ignore
      }
      setIsAuthenticated(false);
      setIsLoading(false);
    };

    const handleCrossTabLogout = (event: StorageEvent): void => {
      if (event.key === AUTH_LOGOUT_BROADCAST_KEY && event.newValue !== null) {
        applyCrossTabLogout();
      }
    };

    const handleChannelLogout = (event: MessageEvent): void => {
      if (event.data?.type === 'logout') {
        applyCrossTabLogout();
      }
    };

    window.addEventListener('storage', handleCrossTabLogout);

    try {
      channel = new BroadcastChannel(AUTH_LOGOUT_BROADCAST_CHANNEL);
      channel.addEventListener('message', handleChannelLogout);
    } catch {
      console.log('BroadcastChannel unsupported in this browser context.');
    }

    return () => {
      window.removeEventListener('storage', handleCrossTabLogout);
      if (channel) {
        channel.removeEventListener('message', handleChannelLogout);
        channel.close();
      }
    };
  }, []);


  useEffect(() => {
    let mounted = true;

    // Move the activity logger + idle monitor startup into a helper.
    // Returns a stop() function which stops both logger and idle monitor.
    const initIdleLogout = (timeoutMs: number) => {
      // start activity timestamp logger (updates lastActivity in sessionStorage)
      const stopLogger = startActivityTimestampLogger();

      // start idle monitor which triggers a forced logout (but no navigation)
      const stopIdle = startIdleMonitor(timeoutMs, async () => {
        // mark that idle logout occurred so we don't auto-redirect elsewhere
        suppressRedirectOnIdleRef.current = true;

        try {
          await authService.logout();
        } catch (e) {
          console.error('Error during logout on idle', e);
        } finally {
          // stop monitors but DO NOT navigate to /login here
          try { stopLogger(); } catch {
            console.log('Error stopping activity logger on idle logout — ignoring.');
          }
          try { stopIdle(); } catch {
            console.log('Error stopping idle monitor on idle logout — ignoring.');
          }

          if (!mounted) return;
          // reflect logged-out state so UI can respond; do not auto-redirect
          setIsAuthenticated(false);
        }
      });

      // return composite stop function
      return () => {
        try { stopIdle(); } catch {}
        try { stopLogger(); } catch {}
      };
    };

    const checkAuthentication = async () => {
      try {
        console.log('ProtectedRoute: Checking authentication...', { pathname: location.pathname });

        const accessToken = sessionStorage.getItem('accessToken');

        if (crossTabLogoutRef.current) {
          if (!mounted) return;
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        if (!accessToken) {
          if (!mounted) return;
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }
        const isAuth = await authService.checkAndRefreshAuth();

        if (crossTabLogoutRef.current) {
          if (!mounted) return;
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        if (isAuth) {
          
          // Fetch profile once after authentication succeeds
          if (!profileFetchedRef.current) {
            dispatch(fetchProfile());
            profileFetchedRef.current = true;
          }

          // previously inline: startLogger + startIdleMonitor...
          // now delegated to helper which returns a stop() function
          if (!idleInitialisedRef.current) {
            stopAllRef.current = initIdleLogout(60 * 60 * 1000); // 60 minutes (1 hour)
            idleInitialisedRef.current = true;

            // Also log that activity tracking has been started now that user is authenticated
          }

          if (!mounted) return;
          setIsAuthenticated(true);
        } else {
          if (!mounted) return;
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ Auth check error:', error);
        if (!mounted) return;
        setIsAuthenticated(false);
      } finally {
        if (!mounted) return;
        setIsLoading(false);
      }
    };

    checkAuthentication();

    return () => {
      mounted = false;
      try { stopAllRef.current?.(); } catch {}
    };
  }, []);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    // If the user was logged out due to idle, do NOT redirect to /login per request.
    if (suppressRedirectOnIdleRef.current) {
      return (
        <div style={{ padding: 20, textAlign: 'center' }}>
          <p>Your session expired due to inactivity.</p>
        </div>
      );
    }

    // Normal unauthenticated flow: save return path and redirect to login.
    try {
      const returnTo = `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
      if (returnTo?.startsWith('/')) {
        sessionStorage.setItem('postLoginRedirect', returnTo);
        console.log('Saved post-login redirect:', returnTo);
      }
    } catch (e) {
      console.warn('Could not persist post-login redirect', e);
    }

    console.log('🔴 Redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  console.log('🟢 Rendering protected content');
  return <>{children}</>;
};

export default ProtectedRoute;
